import { apiUrl } from "../api/endpoints";
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

let cachedTypes = null;
let cachedAvailability = null;
let pendingTypesRequest = null;

export const DEFAULT_AI_REPORT_LOCATION = 'United Arab Emirates';

export const AI_REPORT_PARAM_INPUT_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'select', label: 'Select' },
  { value: 'checkbox', label: 'Checkboxes' },
  { value: 'switch', label: 'Switch' },
];

export const normalizeAiReportApiUrl = (value) =>
  String(value || '').trim().replace(/\/upload-preview(?=\/|\?|#|$)/i, '/upload-pdf');

export const normalizeAiReportParamInputType = (value) => {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-');

  if (!normalized || normalized === 'varchar' || normalized === 'string') return 'text';
  if (['float', 'int', 'integer', 'decimal', 'double'].includes(normalized)) return 'number';
  if (['boolean', 'bool', 'toggle'].includes(normalized)) return 'switch';
  if (['multi-checkbox', 'multicheckbox', 'multi-select', 'multiselect'].includes(normalized)) return 'checkbox';
  return AI_REPORT_PARAM_INPUT_TYPES.some((type) => type.value === normalized) ? normalized : 'text';
};

export const normalizeAiReportParamOptions = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((option) => {
        if (option && typeof option === 'object') {
          const optionValue = String(option.value ?? option.key ?? option.label ?? '').trim();
          if (!optionValue) return null;
          return {
            value: optionValue,
            label: String(option.label ?? optionValue).trim(),
          };
        }

        const optionValue = String(option ?? '').trim();
        return optionValue ? { value: optionValue, label: formatAiReportFieldLabel(optionValue) } : null;
      })
      .filter(Boolean);
  }

  return String(value || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => ({ value: entry, label: formatAiReportFieldLabel(entry) }));
};

export const normalizeAiReportTypes = (value = {}) => {
  const source = Array.isArray(value?.types) ? value.types : [];
  return source
    .map((type) => ({
      value: String(type?.value || '').trim(),
      label: String(type?.label || type?.value || '').trim(),
      apiUrl: normalizeAiReportApiUrl(type?.apiUrl),
      params: (Array.isArray(type?.params) ? type.params : [])
        .map((param) => {
          const key = String(param?.key || '').trim();
          return {
            key,
            label: String(param?.label || param?.key || '').trim(),
            required: param?.required !== false,
            placeholder: String(param?.placeholder || '').trim(),
            inputType: normalizeAiReportParamInputType(param?.inputType ?? param?.type),
            options: normalizeAiReportParamOptions(param?.options ?? param?.values),
            defaultValue: param?.defaultValue === undefined || param?.defaultValue === null ? undefined : String(param.defaultValue),
            system: param?.system === true || param?.hidden === true || key === 'did' || key === 'device_id',
          };
        })
        .filter((param) => param.key),
    }))
    .filter((type) => type.value && type.label && type.apiUrl);
};

export const normalizeAiReportAvailability = (value = {}) => ({
  devices: value.devices !== false,
  dashboards: value.dashboards !== false,
  templates: value.templates !== false,
});

export const buildAiReportTypeOptions = (types = []) =>
  types.map((type) => ({
    value: type.value,
    label: type.label,
  }));

export const getAiReportTypeConfig = (types = [], typeValue) =>
  types.find((type) => type.value === typeValue) || null;

export const buildDefaultAiReportVariables = (types = [], typeValue, options = {}) => {
  const config = getAiReportTypeConfig(types, typeValue);
  const params = Array.isArray(config?.params) ? config.params : [];
  const hasParam = (key) => params.some((param) => param.key === key);
  const defaults = {};
  const roomName = String(options.room || '').trim();

  if (hasParam('location')) defaults.location = DEFAULT_AI_REPORT_LOCATION;
  if (hasParam('room') && roomName) defaults.room = roomName;
  params.forEach((param) => {
    if (param?.system === true) return;
    if (param?.defaultValue !== undefined && param?.defaultValue !== null) {
      defaults[param.key] = String(param.defaultValue);
    }
  });

  return defaults;
};

export const mergeDefaultAiReportVariables = (types = [], typeValue, variables = {}, options = {}) => ({
  ...buildDefaultAiReportVariables(types, typeValue, options),
  ...(variables || {}),
});

export const formatAiReportFieldLabel = (fieldName) =>
  String(fieldName || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const sanitizeAiReportVariablesForType = (types = [], typeValue, variables = {}) => {
  const config = getAiReportTypeConfig(types, typeValue);
  if (!config) return {};

  return (config.params || []).reduce((acc, param) => {
    if (param?.system === true) return acc;
    const inputType = normalizeAiReportParamInputType(param.inputType);
    const value = variables?.[param.key];

    if (inputType === 'checkbox') {
      acc[param.key] = Array.isArray(value) ? value.map((entry) => String(entry).trim()).filter(Boolean).join(',') : String(value ?? '').trim();
      return acc;
    }

    if (inputType === 'switch') {
      acc[param.key] = value === true || value === 'true' || value === '1' || value === 1 ? 'true' : '';
      return acc;
    }

    acc[param.key] = String(value ?? '').trim();
    return acc;
  }, {});
};

export const getMissingRequiredAiReportParams = (types = [], typeValue, variables = {}) => {
  const config = getAiReportTypeConfig(types, typeValue);
  if (!config) return [];

  const sanitizedVariables = sanitizeAiReportVariablesForType(types, typeValue, variables);
  return (config.params || []).filter((param) => {
    if (param?.system === true) return false;
    if (param.required === false) return false;
    return !String(sanitizedVariables[param.key] ?? '').trim();
  });
};

export const buildMissingAiReportParamsMessage = (missingParams = [], labels = {}) => {
  const names = (missingParams || [])
    .map((param) => param.label || formatAiReportFieldLabel(param.key))
    .filter(Boolean);

  if (names.length === 0) return '';
  return `${labels.requiredFieldsMissing || 'Please fill required AI report fields'}: ${names.join(', ')}`;
};

export const setAiReportTypesCache = (types = [], availability = cachedAvailability) => {
  cachedTypes = normalizeAiReportTypes({ types });
  cachedAvailability = normalizeAiReportAvailability(availability);
  pendingTypesRequest = null;
};

export const useAiReportTypes = () => {
  const [types, setTypes] = useState(cachedTypes || []);
  const [availability, setAvailability] = useState(
    cachedAvailability || normalizeAiReportAvailability()
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;

    if (cachedTypes) {
      setTypes(cachedTypes);
      setAvailability(cachedAvailability || normalizeAiReportAvailability());
      return () => {
        active = false;
      };
    }

    setLoading(true);
    pendingTypesRequest =
      pendingTypesRequest ||
      axios
        .get(apiUrl(`configuration/public`))
        .then((res) => ({
          types: normalizeAiReportTypes(res.data?.data?.aiReportTypes),
          availability: normalizeAiReportAvailability(res.data?.data?.aiReportAvailability),
        }))
        .catch((err) => {
          console.warn('[useAiReportTypes] Failed to load AI report types:', err);
          return { types: [], availability: normalizeAiReportAvailability() };
        })
        .then((nextConfig) => {
          cachedTypes = nextConfig.types;
          cachedAvailability = nextConfig.availability;
          pendingTypesRequest = null;
          return nextConfig;
        });

    pendingTypesRequest
      .then((res) => {
        if (!active) return;
        setTypes(res.types);
        setAvailability(res.availability);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const options = useMemo(() => buildAiReportTypeOptions(types), [types]);

  return { types, options, availability, loading };
};
